import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { 
  CheckIcon, 
  XMarkIcon, 
  CloudIcon, 
  DevicePhoneMobileIcon, 
  ShieldCheckIcon,
  BoltIcon,
  GlobeAltIcon,
  DocumentCheckIcon,
  QrCodeIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import DemoModal from '../components/DemoModal';
import FeatureShowcase from '../components/FeatureShowcase';
import SmartAssistant from '../components/SmartAssistant';
import { getImageUrl, hideBrokenImage } from '../utils/imageUrl';
import { API_URL } from '../config/environment';

const LandingPage = () => {
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    fetchBlogs();
    fetchPricingPlans();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await fetch(`${API_URL}/blogs?status=published&limit=3`);
      if (response.ok) {
        const data = await response.json();
        setBlogs(data.blogs || []);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoadingBlogs(false);
    }
  };

  const fetchPricingPlans = async () => {
    try {
      setLoadingPlans(true);
      setPlansError('');
      const response = await fetch(`${API_URL}/auth/subscription-plans`);
      if (!response.ok) {
        throw new Error(`Subscription plans request failed with status ${response.status}`);
      }
      const data = await response.json();
      setPricingPlans(Array.isArray(data.plans) ? data.plans : []);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      setPlansError('Pricing plans are currently unavailable. Please contact support for current packages.');
      setPricingPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const formatPlanPrice = (price) => {
    const numericPrice = Number(price || 0);
    return numericPrice.toLocaleString('en-PK', {
      maximumFractionDigits: numericPrice % 1 === 0 ? 0 : 2
    });
  };

  const getInvoiceLimitText = (limit) => {
    const numericLimit = Number(limit);
    if (numericLimit < 0) return 'Unlimited invoices/month';
    return `${numericLimit.toLocaleString('en-PK')} invoices/month`;
  };

  const getPlanFeatures = (plan) => {
    const features = Array.isArray(plan.features) ? plan.features : [];
    const hasInvoiceLimit = features.some((feature) => /invoice/i.test(String(feature)));
    return hasInvoiceLimit ? features : [getInvoiceLimitText(plan.max_invoices_per_month), ...features];
  };

  const getPlanLink = (plan) => {
    const planCode = encodeURIComponent(plan.code || plan.id || '');
    return user ? `/app/subscription?plan=${planCode}` : `/register?plan=${planCode}`;
  };

  const features = [
    {
      icon: <DocumentCheckIcon className="h-8 w-8" />,
      title: "Fully Compliant with FBR's DI API",
      description: "100% compliant with Pakistan's Federal Board of Revenue Digital Invoicing requirements"
    },
    {
      icon: <BoltIcon className="h-8 w-8" />,
      title: "Real-time Integration",
      description: "Seamless integration with both Sandbox & Production environments"
    },
    {
      icon: <QrCodeIcon className="h-8 w-8" />,
      title: "Auto QR/Logo Printing",
      description: "Automatic QR code generation and logo printing for all invoices"
    },
    {
      icon: <GlobeAltIcon className="h-8 w-8" />,
      title: "No Static IP Needed",
      description: "Access from anywhere without the need for expensive static IP addresses"
    },
    {
      icon: <DevicePhoneMobileIcon className="h-8 w-8" />,
      title: "Multi-Device Support",
      description: "Accessible on PC, Mobile, and Tablet - work from anywhere"
    },
    {
      icon: <CloudIcon className="h-8 w-8" />,
      title: "Cloud-Based Solution",
      description: "Always up-to-date with automatic updates and cloud reliability"
    }
  ];

  const comparisonData = [
    {
      feature: "Static IP Needed",
      local: { value: "Yes (Rs. 750+/mo)", icon: <XMarkIcon className="h-5 w-5 text-red-500" /> },
      fbrstapp: { value: "No", icon: <CheckIcon className="h-5 w-5 text-green-500" /> }
    },
    {
      feature: "Location Locked",
      local: { value: "Yes", icon: <XMarkIcon className="h-5 w-5 text-red-500" /> },
      fbrstapp: { value: "No", icon: <CheckIcon className="h-5 w-5 text-green-500" /> }
    },
    {
      feature: "Installation Needed",
      local: { value: "Yes", icon: <XMarkIcon className="h-5 w-5 text-red-500" /> },
      fbrstapp: { value: "No", icon: <CheckIcon className="h-5 w-5 text-green-500" /> }
    },
    {
      feature: "Auto Updates",
      local: { value: "No", icon: <XMarkIcon className="h-5 w-5 text-red-500" /> },
      fbrstapp: { value: "Yes", icon: <CheckIcon className="h-5 w-5 text-green-500" /> }
    },
    {
      feature: "Multi-device Support",
      local: { value: "No", icon: <XMarkIcon className="h-5 w-5 text-red-500" /> },
      fbrstapp: { value: "Yes", icon: <CheckIcon className="h-5 w-5 text-green-500" /> }
    }
  ];

  const faqs = [
    {
      question: "Do I need to install anything?",
      answer: "No installation required! FBRSTAPP is a cloud-based solution that works directly in your web browser. Simply sign up and start submitting invoices immediately."
    },
    {
      question: "Does it work with FBR Production API?",
      answer: "Yes, FBRSTAPP works seamlessly with both FBR Sandbox (for testing) and Production environments. You can switch between environments as needed."
    },
    {
      question: "Is it secure?",
      answer: "Absolutely! We use 256-bit encryption for all data transmission and token-secured access. Your business data is protected with enterprise-grade security measures."
    },
    {
      question: "Do I need a static IP address?",
      answer: "No static IP required! Unlike traditional local software solutions, FBRSTAPP works from any internet connection, saving you hundreds of rupees monthly on static IP costs."
    },
    {
      question: "Can I use it on mobile devices?",
      answer: "Yes! FBRSTAPP is fully responsive and works perfectly on smartphones, tablets, and desktop computers. Submit invoices from anywhere, anytime."
    },
    {
      question: "How does pricing work?",
      answer: "We offer flexible pricing plans based on your invoice volume. Start with our free trial to test all features, then choose a plan that fits your business needs."
    },
    {
      question: "Can we have a tenant account?",
      answer: "We provide a complete white-label solution that can be fully integrated into your website. Your users will access all services directly from your domain and interface, without seeing our branding or knowing the original platform. This allows each tenant to offer the service under their own name, seamlessly and securely."
    }
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-3 py-4">
            <div className="flex items-center">
              <img 
                src="/fbrlogo1.svg" 
                alt="FBR Logo" 
                className="h-10 w-10 mr-3"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-blue-600">FBRSTAPP</h1>
                <p className="hidden sm:block text-sm text-gray-600">Cloud-Based FBR Digital Invoicing</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm sm:text-base">
              {/* Auth-aware header actions */}
              {user ? (
                <Link
                  to="/app/dashboard"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-2 sm:px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="px-3 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Product Overview */}
      <section id="product" className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Submit Invoices to FBR in{' '}
              <span className="text-blue-600">Real-Time</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-4">
              From Anywhere. Cloud-Based. No Static IP Needed.
            </p>
            <p className="text-lg text-gray-500 mb-8">
              Always Up to Date. Fully Compliant.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Free
              </Link>
              <button 
                onClick={() => setShowDemoModal(true)}
                className="px-8 py-4 border-2 border-blue-600 text-blue-600 text-lg font-semibold rounded-lg hover:bg-blue-50 transition-colors"
              >
                Schedule Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Features */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose FBRSTAPP?
            </h2>
            <p className="text-xl text-gray-600">
              The most advanced cloud-based FBR invoicing solution in Pakistan
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg border hover:shadow-xl transition-shadow">
                <div className="text-blue-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table - Benefits */}
      <section id="benefits" className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              FBRSTAPP vs Local Software
            </h2>
            <p className="text-xl text-gray-600">
              See why businesses are switching to our cloud solution
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-lg font-semibold text-gray-900">
                      Feature
                    </th>
                    <th className="px-6 py-4 text-center text-lg font-semibold text-gray-900">
                      Local Software
                    </th>
                    <th className="px-6 py-4 text-center text-lg font-semibold text-blue-600">
                      FBRSTAPP Cloud
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {comparisonData.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-gray-900 font-medium">
                        {row.feature}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          {row.local.icon}
                          <span className="text-gray-600">{row.local.value}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          {row.fbrstapp.icon}
                          <span className="text-gray-900 font-medium">{row.fbrstapp.value}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots/Demo Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple. Powerful. Effective.
            </h2>
            <p className="text-xl text-gray-600">
              See how easy it is to submit FBR-compliant invoices
            </p>
          </div>

          <FeatureShowcase />
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by Businesses Across Pakistan
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of companies already using FBRSTAPP
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                "FBRSTAPP saved us thousands on static IP costs and made FBR compliance effortless. 
                Our invoices are submitted in seconds!"
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                  A
                </div>
                <div className="ml-3">
                  <p className="font-semibold text-gray-900">Ahmed Khan</p>
                  <p className="text-sm text-gray-600">CEO, Tech Solutions Ltd</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                "The mobile access is a game-changer. I can submit invoices from anywhere, 
                and the QR codes print perfectly every time."
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold">
                  S
                </div>
                <div className="ml-3">
                  <p className="font-semibold text-gray-900">Sarah Ali</p>
                  <p className="text-sm text-gray-600">Owner, Textile Exports</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                "No more software installations or updates. FBRSTAPP handles everything 
                in the cloud. It's the future of FBR compliance."
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                  M
                </div>
                <div className="ml-3">
                  <p className="font-semibold text-gray-900">Muhammad Hassan</p>
                  <p className="text-sm text-gray-600">CFO, Manufacturing Co</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about FBRSTAPP
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border">
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50"
                  onClick={() => toggleFaq(index)}
                >
                  <span className="text-lg font-semibold text-gray-900">
                    {faq.question}
                  </span>
                  {expandedFaq === index ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 mb-12">
            Start free, scale as you grow. No hidden fees, no setup costs.
          </p>

          {loadingPlans ? (
            <div className="bg-gray-50 rounded-xl border-2 border-gray-200 p-8 text-gray-600">
              Loading current pricing plans...
            </div>
          ) : plansError ? (
            <div className="bg-yellow-50 rounded-xl border-2 border-yellow-200 p-8 text-yellow-800">
              {plansError}
            </div>
          ) : pricingPlans.length === 0 ? (
            <div className="bg-gray-50 rounded-xl border-2 border-gray-200 p-8 text-gray-600">
              No active pricing plans are available right now.
            </div>
          ) : (
            <div className={`grid grid-cols-1 gap-8 ${
              pricingPlans.length === 1 ? 'md:grid-cols-1 max-w-sm mx-auto' :
              pricingPlans.length === 2 ? 'md:grid-cols-2 max-w-2xl mx-auto' :
              'md:grid-cols-3'
            }`}>
              {pricingPlans.map((plan) => {
                const isRecommended = Boolean(plan.is_recommended);
                return (
                  <div
                    key={plan.id || plan.code}
                    className={`rounded-xl p-8 border-2 relative ${
                      isRecommended
                        ? 'bg-blue-50 border-blue-500'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    {isRecommended && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                          Most Popular
                        </span>
                      </div>
                    )}
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="text-3xl font-bold text-gray-900 mb-4">
                      Rs. {formatPlanPrice(plan.price)}
                      <span className="text-lg font-normal text-gray-600">/month</span>
                    </div>
                    {plan.description && (
                      <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                    )}
                    <ul className="text-left space-y-2 mb-6">
                      {getPlanFeatures(plan).map((feature, index) => (
                        <li key={index} className="flex items-center text-gray-600">
                          <CheckIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={getPlanLink(plan)}
                      className={`w-full block px-6 py-3 rounded-lg font-medium text-center ${
                        isRecommended
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                      }`}
                    >
                      Select Plan
                    </Link>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              💡 <strong>Save Rs. 9,600+ annually</strong> compared to static IP + local software costs
            </p>
            <p className="text-sm text-gray-500">
              All plans include automatic updates, cloud backup, and 99.9% uptime guarantee
            </p>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Latest from Our Blog
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Stay updated with FBR compliance tips, invoicing guides, and tax updates in Pakistan
            </p>
          </div>
          
          <div>
            {loadingBlogs ? (
              <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-gray-100">
                <p className="text-gray-500 font-medium">Loading latest articles...</p>
              </div>
            ) : blogs.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
                {blogs.map((blog) => {
                  const imageUrl = getImageUrl(blog.featured_image);
                  return (
                    <div key={blog.id} className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col group">
                      <Link to={`/blog/${blog.slug}`} className="block h-52 w-full overflow-hidden bg-gray-100 relative">
                        {imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt={blog.title}
                            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={hideBrokenImage}
                          />
                        ) : (
                          <div className="h-full w-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center p-6 text-white text-center font-bold text-lg">
                            FBRSTAPP
                          </div>
                        )}
                        {blog.category_name && (
                          <span className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-sm text-blue-700 rounded-full text-xs font-bold shadow-sm">
                            {blog.category_name}
                          </span>
                        )}
                      </Link>
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="text-xs text-gray-400 mb-2">
                          {new Date(blog.publish_date || Date.now()).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition leading-snug">
                          <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                        </h3>
                        <p className="text-gray-600 text-sm flex-1 mb-4 line-clamp-3">
                          {blog.excerpt || (blog.content ? blog.content.replace(/<[^>]*>?/gm, '').substring(0, 120) : '')}...
                        </p>
                        <Link 
                          to={`/blog/${blog.slug}`} 
                          className="text-blue-600 font-bold text-sm hover:text-blue-700 mt-auto inline-flex items-center group-hover:translate-x-1 transition-transform"
                        >
                          Read Article →
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 text-gray-500 mb-8">
                <p>No blog posts available yet.</p>
              </div>
            )}
          </div>

          <div className="text-center">
            <Link
              to="/blog"
              className="inline-block px-8 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 shadow-md hover:shadow-lg transition-all"
            >
              View All Blog Posts
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Simplify FBR Compliance?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of businesses already using FBRSTAPP for hassle-free invoice submission
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Using FBRSTAPP Today
            </Link>
            <button 
              onClick={() => setShowDemoModal(true)}
              className="px-8 py-4 border-2 border-white text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              Get a Demo
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/fbrlogo1.svg" 
                  alt="FBR Logo" 
                  className="h-8 w-8 mr-3"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <h3 className="text-xl font-bold">FBRSTAPP</h3>
              </div>
              <p className="text-gray-400 mb-4">
                Pakistan's leading cloud-based FBR Digital Invoicing solution. 
                Submit invoices to FBR in real-time from anywhere.
                For more Information Please write us{' '}
                <a 
                  href="mailto:info@fbrstapp.com" 
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  info@fbrstapp.com
                </a>
                {' '}WhatsApp{' '}
                <a 
                  href="https://wa.me/923080323323" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-green-400 hover:text-green-300 underline"
                >
                  +92-308-0323323
                </a>
              </p>
              <div className="flex items-center text-sm text-gray-400">
                <ShieldCheckIcon className="h-4 w-4 mr-2" />
                256-bit encrypted, token-secured access
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white text-left">Features</button></li>
                <li><button onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white text-left">Pricing</button></li>
                <li><button onClick={() => document.getElementById('benefits')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white text-left">Benefits</button></li>
                <li><Link to="/register" className="hover:text-white text-left">Get Started</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/blog" className="hover:text-white text-left">Blog</Link></li>
                <li><Link to="/help-center" className="hover:text-white text-left">Help Center</Link></li>
                <li><Link to="/contact-us" className="hover:text-white text-left">Contact Us</Link></li>
                <li><Link to="/training" className="hover:text-white text-left">Training</Link></li>
                <li><Link to="/community" className="hover:text-white text-left">Community</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} FBRSTAPP. All rights reserved. | Compliant with FBR Digital Invoicing Requirements</p>
          </div>
        </div>
      </footer>

      {/* Demo Modal */}
      <DemoModal 
        isOpen={showDemoModal} 
        onClose={() => setShowDemoModal(false)} 
      />

      {/* Smart Assistant */}
      <SmartAssistant />
    </div>
  );
};

export default LandingPage;
